import { useState, useEffect, useCallback, useRef } from "react";
import protobuf from "protobufjs";

/* ======================= Protobuf схема (как в твоём коде) ======================= */
const root = protobuf.Root.fromJSON({
  nested: {
    PushDataV3ApiWrapper: {
      fields: {
        channel: { type: "string", id: 1 },
        symbol: { type: "string", id: 3, optional: true },
        symbolId: { type: "string", id: 4, optional: true },
        createTime: { type: "int64", id: 5, optional: true },
        sendTime: { type: "int64", id: 6, optional: true },
        publicAggreDepths: { type: "PublicAggreDepthsV3Api", id: 313 },
        publicDepthFull: { type: "PublicDepthFullV3Api", id: 314 },
      },
      oneofs: {
        body: [
          "publicDeals",
          "publicIncreaseDepths",
          "publicLimitDepths",
          "privateOrders",
          "publicBookTicker",
          "privateDeals",
          "privateAccount",
          "publicSpotKline",
          "publicMiniTicker",
          "publicMiniTickers",
          "publicBookTickerBatch",
          "publicIncreaseDepthsBatch",
          "publicAggreDepths",
          "publicAggreDeals",
          "publicAggreBookTicker",
        ],
      },
    },
    PublicDepthFullV3Api: {
      fields: {
        asks: { rule: "repeated", type: "PublicAggreDepthV3ApiItem", id: 1 },
        bids: { rule: "repeated", type: "PublicAggreDepthV3ApiItem", id: 2 },
      },
    },
    PublicAggreDepthsV3Api: {
      fields: {
        asks: { rule: "repeated", type: "PublicAggreDepthV3ApiItem", id: 1 },
        bids: { rule: "repeated", type: "PublicAggreDepthV3ApiItem", id: 2 },
        eventType: { type: "string", id: 3 },
        fromVersion: { type: "string", id: 4 },
        toVersion: { type: "string", id: 5 },
      },
    },
    PublicAggreDepthV3ApiItem: {
      fields: {
        price: { type: "string", id: 1 },
        quantity: { type: "string", id: 2 },
      },
    },
  },
});

const PushDataV3ApiWrapper = root.lookupType("PushDataV3ApiWrapper");
const PROXY_ORIGIN = process.env.REACT_APP_BACKEND_URL;
const viaProxy = (absUrl) => `${PROXY_ORIGIN}/proxy/${encodeURI(absUrl)}`;

/* ======================= Декодер protobuf-сообщений ======================= */
async function decodeMexcSpotData(blob) {
  try {
    const arrayBuffer = await blob.arrayBuffer();
    const uint8Array = new Uint8Array(arrayBuffer);
    const message = PushDataV3ApiWrapper.decode(uint8Array);
    const object = PushDataV3ApiWrapper.toObject(message, {
      longs: String,
      enums: String,
      bytes: String,
      defaults: true,
    });
    return object;
  } catch (e) {
    console.error("❌ [MEXC SPOT] Ошибка декодирования protobuf:", e);
    return null;
  }
}

/* ======================= Хук ======================= */
export const useMexcSpot = (symbol) => {
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState(null);

  // стакан
  const [orderBook, setOrderBook] = useState({ asks: [], bids: [] });
  const orderBookRef = useRef({ asks: [], bids: [] });
  const hasInitialBook = useRef(false);
  const deltaBufferRef = useRef([]); // дельты до загрузки снапшота
  const animationFrameRef = useRef(null);

  // тикер (для спота нам нужна цена и 24ч объём)
  const [tickerData, setTickerData] = useState(null);
  const tickerTimerRef = useRef(null);

  // ws
  const wsRef = useRef(null);

  /* ---------------- Применение инкрементальных обновлений стакана ---------------- */
  const applyDeltas = useCallback((updates, side) => {
    const book = [...orderBookRef.current[side]];

    // Сначала удаляем нули
    updates.forEach(([price, quantity]) => {
      if (parseFloat(quantity) === 0) {
        const idx = book.findIndex(([p]) => parseFloat(p) === parseFloat(price));
        if (idx !== -1) book.splice(idx, 1);
      }
    });

    // Затем добавляем/обновляем > 0
    updates.forEach(([price, quantity]) => {
      const qtyNum = parseFloat(quantity);
      if (qtyNum > 0) {
        const priceNum = parseFloat(price);
        const idx = book.findIndex(([p]) => parseFloat(p) === priceNum);
        if (idx !== -1) {
          book[idx] = [price, quantity];
        } else {
          if (side === "asks") {
            // asks: по возрастанию
            const insertIndex = book.findIndex(([p]) => parseFloat(p) > priceNum);
            if (insertIndex === -1) book.push([price, quantity]);
            else book.splice(insertIndex, 0, [price, quantity]);
          } else {
            // bids: по убыванию
            const insertIndex = book.findIndex(([p]) => parseFloat(p) < priceNum);
            if (insertIndex === -1) book.push([price, quantity]);
            else book.splice(insertIndex, 0, [price, quantity]);
          }
        }
      }
    });

    orderBookRef.current[side] =
      side === "asks"
        ? book.sort((a, b) => parseFloat(a[0]) - parseFloat(b[0])).slice(0, 20)
        : book.sort((a, b) => parseFloat(b[0]) - parseFloat(a[0])).slice(0, 20);

    if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
    animationFrameRef.current = requestAnimationFrame(() => {
      setOrderBook({ ...orderBookRef.current });
    });
  }, []);

  /* ---------------- Применение буфера дельт ---------------- */
  const flushDeltaBuffer = useCallback(() => {
    if (!deltaBufferRef.current.length) return;
    console.log(`📦 [MEXC SPOT] Применяем буфер дельт:`, deltaBufferRef.current.length);

    for (const depth of deltaBufferRef.current) {
      if (!depth) continue;
      const asksUpdates = (depth.asks || []).map(({ price, quantity }) => [price, quantity]);
      const bidsUpdates = (depth.bids || []).map(({ price, quantity }) => [price, quantity]);
      if (asksUpdates.length) applyDeltas(asksUpdates, "asks");
      if (bidsUpdates.length) applyDeltas(bidsUpdates, "bids");
    }
    deltaBufferRef.current.length = 0;
  }, [applyDeltas]);

  /* ---------------- Загрузка начального стакана по HTTP ---------------- */
  const loadInitialSnapshot = useCallback(async () => {
    if (!symbol) return;
    try {
      const formattedSymbol = symbol.includes("USDT") ? symbol : `${symbol}USDT`;
      const abs = `https://api.mexc.com/api/v3/depth?symbol=${formattedSymbol}&depth=50`;
      const url = viaProxy(abs);

      // const url = `https://api.mexc.com/api/v3/depth?symbol=${formattedSymbol}&depth=50`;

      console.log("🌐 [MEXC SPOT] Загружаем начальный стакан:", url);
      const res = await fetch(url, { headers: { 'ngrok-skip-browser-warning': '1' } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();

      const asks = (json.asks || [])
        .map(([p, q]) => [String(p), String(q)])
        .filter(([, q]) => parseFloat(q) > 0)
        .sort((a, b) => parseFloat(a[0]) - parseFloat(b[0]))
        .slice(0, 20);

      const bids = (json.bids || [])
        .map(([p, q]) => [String(p), String(q)])
        .filter(([, q]) => parseFloat(q) > 0)
        .sort((a, b) => parseFloat(b[0]) - parseFloat(a[0]))
        .slice(0, 20);

      orderBookRef.current = { asks, bids };
      hasInitialBook.current = true;
      setOrderBook({ ...orderBookRef.current });

      console.log("✅ [MEXC SPOT] Начальный стакан загружен (HTTP). Применяем буфер дельт...");
      flushDeltaBuffer();
    } catch (e) {
      console.error("❌ [MEXC SPOT] Ошибка загрузки начального стакана:", e);
      setError(e?.message || "Ошибка загрузки стакана");
    }
  }, [symbol, flushDeltaBuffer]);

  /* ---------------- 24h Ticker (REST) — lastPrice и 24h volume ---------------- */
  const fetchTicker24h = useCallback(async () => {
    if (!symbol) return;
    try {
      const formattedSymbol = symbol.includes("USDT") ? symbol : `${symbol}USDT`;
      const abs = `https://api.mexc.com/api/v3/ticker/24hr?symbol=${formattedSymbol}`;
      const url = viaProxy(abs);
      const res = await fetch(url, { headers: { 'ngrok-skip-browser-warning': '1' } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const j = await res.json();

      const last =
        j.lastPrice ?? j.last ?? j.close ?? j.c ?? j.price;
      // для спота чаще удобнее quoteVolume (в USDT), если нет — берём volume
      const volQ = j.quoteVolume ?? j.quoteVolume24h ?? undefined;
      const volB = j.volume ?? j.baseVolume ?? undefined;
      const vol = volQ ?? volB;

      setTickerData((prev) => ({
        ...(prev || {}),
        symbol: formattedSymbol,
        lastPrice: last != null ? String(last) : (prev?.lastPrice || ""),
        // оставляю оба поля — твой UI может читать и volume, и volume24
        volume: vol != null ? String(vol) : (prev?.volume || ""),
        volume24: vol != null ? String(vol) : (prev?.volume24 || ""),
      }));
    } catch (e) {
      console.error("❌ [MEXC SPOT] fetch 24h ticker failed:", e);
    }
  }, [symbol]);

  /* ---------------- WebSocket message handler ---------------- */
  const handleMessage = useCallback(
    async (event) => {
      try {
        // ACK/пинги приходят строкой — парсим если это JSON
        if (typeof event.data === "string") {
          try {
            const msg = JSON.parse(event.data);
            if (msg && typeof msg === "object") {
              console.log("📥 [MEXC SPOT] JSON:", msg);
              if (msg.code === 0) {
                console.log("✅ [MEXC SPOT] Подписка подтверждена:", msg.msg);
              }
            }
          } catch {
            // текстовые пинги/прочее — игнор
          }
          return;
        }

        // Протобаф (Blob)
        if (event.data instanceof Blob) {
          const decoded = await decodeMexcSpotData(event.data);
          if (!decoded) return;

          const aggDepth = decoded.publicAggreDepths;
          if (!aggDepth) return;

          // до снапшота — складываем в буфер
          if (!hasInitialBook.current) {
            deltaBufferRef.current.push(aggDepth);
            return;
          }

          // инкрементальные обновления
          const asksUpdates = (aggDepth.asks || []).map(({ price, quantity }) => [price, quantity]);
          const bidsUpdates = (aggDepth.bids || []).map(({ price, quantity }) => [price, quantity]);

          if (asksUpdates.length) applyDeltas(asksUpdates, "asks");
          if (bidsUpdates.length) applyDeltas(bidsUpdates, "bids");
          return;
        }

        console.log("[MEXC SPOT] Неожиданный тип event.data:", typeof event.data, event.data);
      } catch (err) {
        console.error("❌ [MEXC SPOT] Ошибка обработки сообщения:", err);
        try {
          console.error("Данные сообщения (toString):", String(event.data));
        } catch {}
      }
    },
    [applyDeltas]
  );

  /* ---------------- WebSocket open/error/close ---------------- */
  const handleOpen = useCallback(
    (ws) => {
      if (!symbol) return;

      setIsConnected(true);
      setError(null);

      const formattedSymbol = symbol.includes("USDT") ? symbol : `${symbol}USDT`;
      const msg = {
        method: "SUBSCRIPTION",
        params: [`spot@public.aggre.depth.v3.api.pb@100ms@${formattedSymbol}`],
      };

      ws.send(JSON.stringify(msg));
      console.log("📡 [MEXC SPOT] Подписка отправлена:", msg);
    },
    [symbol]
  );

  const handleError = useCallback((err) => {
    console.error("❌ [MEXC SPOT] WS ошибка:", err);
    setIsConnected(false);
    setError(err?.message || "Ошибка WebSocket");
  }, []);

  const handleClose = useCallback(() => {
    setIsConnected(false);
    console.log("🔌 [MEXC SPOT] WS закрыт");
  }, []);

  /* ---------------- Жизненный цикл ---------------- */
  useEffect(() => {
    if (!symbol) return;

    // мягкий ресет
    hasInitialBook.current = false;
    deltaBufferRef.current.length = 0;
    orderBookRef.current = { asks: [], bids: [] };
    setOrderBook({ asks: [], bids: [] });
    setTickerData(null);
    setError(null);

    // WS
    const WS_URL = "wss://wbs-api.mexc.com/ws";
    const ws = new WebSocket(WS_URL);
    wsRef.current = ws;

    ws.onopen = () => handleOpen(ws);
    ws.onmessage = handleMessage;
    ws.onerror = handleError;
    ws.onclose = handleClose;

    // HTTP: снапшот стакана и опрос 24h тикера
    loadInitialSnapshot();
    fetchTicker24h();
    tickerTimerRef.current = setInterval(fetchTicker24h, 5000);

    return () => {
      try {
        if (ws.readyState === WebSocket.OPEN) ws.close();
      } catch {}
      wsRef.current = null;

      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      if (tickerTimerRef.current) {
        clearInterval(tickerTimerRef.current);
        tickerTimerRef.current = null;
      }
    };
  }, [
    symbol,
    handleOpen,
    handleMessage,
    handleError,
    handleClose,
    loadInitialSnapshot,
    fetchTicker24h,
  ]);

  return { isConnected, error, orderBook, tickerData };
};
